// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LinesHeader from "@tps/App/Components/Cart/LinesHeader";
import cartLinesWarehouseSharedStyles, {
    CartLinesWarehouseStyles,
} from "@tps/App/SharedStyles/CartLinesWarehouseStyles";
import CartLineCardExpanded from "@tps/Overrides/Widgets/Cart/CartLineCardExpanded";
import { cartLinesStyles } from "@tps/Overrides/Widgets/Cart/CartLines";
import { CustomWarehouseDto, ShippingDetailsModel } from "@tps/XDL/types";
import { mergeWithCss } from "@tps/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// This component will be used in order confirmation page and saved order details page

const mapDispatchToProps = {
    loadRealTimeInventory,
    updateCartLine,
    removeCartLine,
    loadRealTimePricing,
};

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    extendedStyles?: CustomCartLinesStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomCartLinesStyles {
    wrapper?: InjectableCss;
    cartLinesWarehouseStyles?: CartLinesWarehouseStyles;
}

export const customCartLinesStyles: CustomCartLinesStyles = {
    wrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    cartLinesWarehouseStyles: {
        ...cartLinesWarehouseSharedStyles,
    },
};

const StyledSection = getStyledWrapper("section");

interface Warehouse {
    id: string;
    approximateWeight: string;
    extendedPriceWithDiscountPerWarehouse: string;
    qtySubTotal: string;
    description: string;
    shipVia: string;
    shippingMethod: string;
    shippingTerms: string;
    notes: string;
}

const CustomCartLines: React.FunctionComponent<Props> = ({
    cart,
    promotions,
    loadRealTimeInventory,
    updateCartLine,
    settingsCollection,
    extendedStyles,
    loadRealTimePricing,
}) => {
    const [styles] = React.useState(() => mergeWithCss(customCartLinesStyles, extendedStyles));

    const { productSettings } = settingsCollection;
    if (!cart || !cart.cartLines) {
        return null;
    }

    const [warehouses, setWarehouses] = useState([] as Warehouse[]);
    const [updatedCartLines, setUpdatedCartLines] = useState<CartLineModel[]>(cart?.cartLines || []);

    useEffect(() => {
        const warehouses: Warehouse[] = [];
        let warehousesObj: SafeDictionary<{
            Description: string;
            ShipVia: string;
            ShippingMethod: string;
            ShippingTerms: string;
            Notes: string;
        }> = {};
        let approximateWeightObj: SafeDictionary<string> = {};
        let extendedPriceWithDiscountPerWarehouseObj: SafeDictionary<string> = {};
        let qtySubTotalObj: SafeDictionary<string> = {};

        if (cart?.properties?.warehouses) {
            warehousesObj = JSON.parse(cart?.properties?.warehouses);
        }
        if (cart?.properties?.approximateWeight) {
            approximateWeightObj = JSON.parse(cart?.properties?.approximateWeight);
        }
        if (cart?.properties?.extendedPriceWithDiscountPerWarehouse) {
            extendedPriceWithDiscountPerWarehouseObj = JSON.parse(
                cart?.properties?.extendedPriceWithDiscountPerWarehouse,
            );
        }
        if (cart?.properties?.qtySubTotal) {
            qtySubTotalObj = JSON.parse(cart?.properties?.qtySubTotal);
        }

        const keys = Object.keys(warehousesObj);
        keys.forEach(key => {
            const warehouse: Warehouse = {
                id: key,
                approximateWeight: approximateWeightObj[key] || "",
                extendedPriceWithDiscountPerWarehouse: extendedPriceWithDiscountPerWarehouseObj[key] || "",
                qtySubTotal: qtySubTotalObj[key] || "",
                description: warehousesObj[key]?.Description || "",
                shipVia: warehousesObj[key]?.ShipVia || "",
                shippingMethod: warehousesObj[key]?.ShippingMethod || "",
                shippingTerms: warehousesObj[key]?.ShippingTerms || "",
                notes: warehousesObj[key]?.Notes || "",
            };
            warehouses.push(warehouse);
        });
        setWarehouses(warehouses);
    }, [cart?.properties]);

    useEffect(() => {
        if (cart && cart.cartLines && cart.cartLines.length && cart.cartLines.some(o => o.productId)) {
            // load inventory and inject it
            const productIds = cart.cartLines.filter(o => o.productId).map(o => o.productId) as string[];
            const filteredProductIds: string[] = [];
            productIds?.forEach(productId => {
                if (filteredProductIds?.indexOf(productId) === -1) {
                    filteredProductIds.push(productId);
                }
            });
            const lines: CartLineModel[] = cloneDeep(cart.cartLines);
            if (!lines) {
                setUpdatedCartLines([]);
                return;
            }

            // TPS-789 : Call realtime price before inventory, so that inventory will be cached
            const productsInfos: ProductInfo[] = [];
            cart.cartLines.forEach(cartLine => {
                productsInfos.push({
                    productId: cartLine.productId || "",
                    qtyOrdered: cartLine.qtyOrdered || 0,
                    unitOfMeasure: cartLine.unitOfMeasure,
                    productDetailPath: cartLine.productUri,
                });
            });
            loadRealTimePricing({
                productPriceParameters: productsInfos,
                onComplete(realTimePricingProps) {
                    loadRealTimeInventory({
                        productIds: filteredProductIds,
                        onSuccess: realTimeInventory => {
                            if (realTimeInventory.realTimeInventoryResults) {
                                realTimeInventory.realTimeInventoryResults?.forEach(inventory => {
                                    lines?.forEach(cartLine => {
                                        if (cartLine.productId === inventory.productId) {
                                            if (!cartLine.properties) {
                                                cartLine.properties = {};
                                            }
                                            (cartLine as any).properties.qtyOnAllBranches = inventory.qtyOnHand;
                                            const warehouseDtos =
                                                (inventory?.inventoryWarehousesDtos?.find(
                                                    o =>
                                                        o.unitOfMeasure.toLowerCase() ===
                                                        (cartLine.unitOfMeasure?.toLowerCase() || ""),
                                                )?.warehouseDtos as CustomWarehouseDto[]) || undefined;
                                            (cartLine as any).properties.qtyOnCurrentBrach = warehouseDtos?.find(
                                                warehouseDto =>
                                                    warehouseDto?.properties?.warehouseId ===
                                                    cartLine.properties.warehouseId,
                                            )?.qty;
                                        }
                                    });
                                });
                            }
                            setUpdatedCartLines(lines);
                        },
                        onError: () => {
                            setUpdatedCartLines(lines);
                        },
                    });
                },
            });
        }
    }, [cart?.cartLines]);

    return (
        <StyledWrapper {...styles.cartLinesWarehouseStyles?.wrapper} data-test-selector={`cartLinesList_${cart.id}`}>
            {warehouses.map(warehouse => {
                return (
                    <StyledSection
                        key={warehouse.id}
                        className="cartlines-warehouse-container"
                        {...styles.cartLinesWarehouseStyles?.warehouseGroup}
                    >
                        <LinesHeader leftTitle={`Branch: ${warehouse.description}`} />
                        {updatedCartLines?.map(cartLine => {
                            const cartLinePromotions = promotions?.filter(
                                promotion => promotion.orderLineId === cartLine.id,
                            );

                            if (cartLine.properties.warehouseId !== warehouse.id) {
                                return;
                            }
                            return (
                                <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                                    <CartLineCardExpanded
                                        cart={cart}
                                        promotions={cartLinePromotions || []}
                                        productSettings={productSettings}
                                        showInventoryAvailability={true}
                                        showLineNotes={false}
                                        showImage={true}
                                        showProductTitle={true}
                                        showBrand={true}
                                        showPerUnitPrice={true}
                                        showPriceLabel={true}
                                        showQtyLabel={false}
                                        showAddToList={!cartLine.isRestricted}
                                        showProductNumber={true}
                                        updateCartLine={updateCartLine}
                                        removeCartLine={removeCartLine}
                                        showRemoveAction={false}
                                        showDistributeOrderButton={false}
                                        showAvailableQty={true}
                                        editable={false}
                                    />
                                </CartLineContext.Provider>
                            );
                        })}
                        <StyledWrapper {...styles.cartLinesWarehouseStyles?.warehouseFooter}>
                            {warehouse.shipVia && (
                                <StyledWrapper {...cartLinesWarehouseSharedStyles.shippingDetailsSection}>
                                    <Typography {...cartLinesWarehouseSharedStyles.shippingDetailsTitle}>
                                        {translate("Shipping Details:")}
                                    </Typography>
                                    <StyledWrapper {...cartLinesWarehouseSharedStyles.selectedOptionsWrapper}>
                                        {warehouse.shipVia && (
                                            <SmallHeadingAndText
                                                {...cartLinesWarehouseSharedStyles.shippingOptionStyle}
                                                heading={translate("Ship Via:")}
                                                text={warehouse.shipVia}
                                            ></SmallHeadingAndText>
                                        )}

                                        {warehouse.shippingMethod && (
                                            <SmallHeadingAndText
                                                {...cartLinesWarehouseSharedStyles.shippingOptionStyle}
                                                heading={translate("Shipping Method:")}
                                                text={warehouse.shippingMethod}
                                            ></SmallHeadingAndText>
                                        )}

                                        {warehouse.shipVia && warehouse.shippingTerms && (
                                            <SmallHeadingAndText
                                                {...cartLinesWarehouseSharedStyles.shippingOptionStyle}
                                                heading={translate("Shipping Terms:")}
                                                text={warehouse.shippingTerms}
                                            ></SmallHeadingAndText>
                                        )}
                                    </StyledWrapper>
                                    {warehouse.notes && (
                                        <SmallHeadingAndText
                                            {...cartLinesStyles.shippingCommentStyle}
                                            heading={translate("Shipping Comment:")}
                                            text={warehouse.notes}
                                        ></SmallHeadingAndText>
                                    )}
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.cartLinesWarehouseStyles?.warehouseSubTotalWeightWrapper}>
                                <Typography {...styles.cartLinesWarehouseStyles?.warehouseSubTotalTypo}>
                                    {siteMessage("Custom_Warehouse_Total", warehouse.qtySubTotal)}
                                    <b>{warehouse.extendedPriceWithDiscountPerWarehouse}</b>
                                </Typography>
                                <Typography {...styles.cartLinesWarehouseStyles?.warehouseWeightTypo}>
                                    {translate("Approximate Weight")}:
                                    <Typography {...styles.cartLinesWarehouseStyles?.unit}>
                                        &nbsp;{warehouse.approximateWeight}&nbsp;
                                        {siteMessage("Custom_Weight_Unit")}
                                    </Typography>
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledSection>
                );
            })}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomCartLines);
